// Shared shape/validation helpers for the "parking instructions" field
// (PP-492). This field can hold either a legacy plain string (pre-PP-492
// data, or a host's live free-text edit via SummaryGrid) or the new
// structured, per-approach-road shape. Every reader must gate on
// isStructuredInstructions() before treating a value as an object — see the
// PP-492 plan for the full list of call sites. Mirrors
// server/helpers/parkingInstructions.js (no shared package between
// client/native/server, so this is intentionally duplicated).

export const INSTRUCTIONS_SCHEMA_VERSION = 2;
export const MAX_APPROACHES = 4;
export const MAX_APPROACH_TEXT_LEN = 500;
// Legacy plain-string cap (SummaryGrid free-text drawer path, and the
// server-side mirror of it in routes/listings.js). Must be able to hold
// flattenInstructions()'s worst case: MAX_APPROACHES entries, each up to
// MAX_APPROACH_TEXT_LEN chars plus a "From <roadLabel>: " prefix and a
// "\n\n" separator between entries — not just an arbitrary round number.
export const MAX_FLATTENED_INSTRUCTIONS_LEN = 2200;
// PP-488 wrote this literal string into confirmedFields.instructions for any
// listing touched during the ~7-week "coming soon" interim cutover, before
// AI_DIRECTIONS_PLACEHOLDER was removed by PP-492. Mirrors the same constant
// in server/helpers/parkingInstructions.js.
export const LEGACY_PP488_PLACEHOLDER = 'AI parking instructions — coming soon';

export function isStructuredInstructions(value) {
    return Boolean(value)
        && typeof value === 'object'
        && value.schemaVersion === INSTRUCTIONS_SCHEMA_VERSION
        && Array.isArray(value.approaches);
}

/**
 * Normalizes either shape into the flat string legacy consumers (textareas,
 * truncated previews) expect. Never throws; unrecognized input becomes ''.
 */
export function flattenInstructions(value) {
    if (value == null) return '';
    // #405: the PP-488 stop-gap sentence is not directions. Treating it as
    // none here covers every page and every copy onto a property.
    if (typeof value === 'string') return value === LEGACY_PP488_PLACEHOLDER ? '' : value;
    if (!isStructuredInstructions(value)) return '';

    return value.approaches
        .filter((a) => a && typeof a.text === 'string' && a.text.trim())
        .map((a) => (a.roadLabel ? `From ${a.roadLabel}: ${a.text.trim()}` : a.text.trim()))
        .join('\n\n');
}

/**
 * Normalizes either shape into a uniform [{roadLabel, text}] array so display
 * components never need to branch on type themselves. A legacy string
 * becomes a single { roadLabel: null, text } entry. Never throws.
 */
export function getDisplayApproaches(value) {
    if (value == null) return [];
    if (typeof value === 'string') {
        if (value === LEGACY_PP488_PLACEHOLDER) return []; // #405, see flattenInstructions
        return value.trim() ? [{ roadLabel: null, text: value }] : [];
    }
    if (!isStructuredInstructions(value)) return [];

    return value.approaches.filter((a) => a && typeof a.text === 'string' && a.text.trim());
}

/**
 * Resolves the confirmed-or-AI-suggested "instructions" value into whichever
 * shape it may be — mirrors server/helpers/parkingInstructions.js's
 * resolveInstructionsSource so every admin/preview surface derives the same
 * thing from the same Listing.
 */
export function resolveInstructionsSource(listing) {
    const raw = listing.confirmedFields?.instructions ?? listing.enrichmentFields?.instructions ?? null;
    if (raw === LEGACY_PP488_PLACEHOLDER) return null;
    if (raw && typeof raw === 'object' && !isStructuredInstructions(raw) && typeof raw.value === 'string') {
        return raw.value === LEGACY_PP488_PLACEHOLDER ? null : raw.value;
    }
    return raw;
}

export function validateStructuredInstructions(value) {
    if (!value || typeof value !== 'object') return 'instructions must be an object or string.';
    if (value.schemaVersion !== INSTRUCTIONS_SCHEMA_VERSION) return 'instructions.schemaVersion is invalid.';
    if (!Array.isArray(value.approaches) || value.approaches.length === 0) {
        return 'instructions.approaches must be a non-empty array.';
    }
    if (value.approaches.length > MAX_APPROACHES) {
        return `instructions.approaches cannot exceed ${MAX_APPROACHES} entries.`;
    }
    for (const approach of value.approaches) {
        if (!approach || typeof approach !== 'object') return 'instructions.approaches entries must be objects.';
        if (typeof approach.text !== 'string' || !approach.text.trim()) {
            return 'instructions.approaches entries must have non-empty text.';
        }
        if (approach.text.length > MAX_APPROACH_TEXT_LEN) {
            return `instructions.approaches text cannot exceed ${MAX_APPROACH_TEXT_LEN} characters.`;
        }
        if (approach.roadLabel != null && typeof approach.roadLabel !== 'string') {
            return 'instructions.approaches.roadLabel must be a string or null.';
        }
    }
    return null;
}
